import { useContext, useRef, useState } from "react";
import styled from "styled-components";
import client from "api/WookAxios";
import ModalContext from "components/contexts/ModalContext";
import log from "loglevel";
import { parseErrors } from "lib/errors/parse";
import { toast } from "sonner";
import { useLocation, useNavigate } from "react-router-dom";
import { handleThrottle } from "lib/errors/handle";
import { useVerification } from "../useVerification";
import { VERIFICATION_CHANNEL } from "constants/auth";
import { ResetPasswordForm } from "./ResetPasswordForm";
import { ResetPasswordVerify } from "./ResetPasswordVerify";
import { sessionStorageAdapter } from "utils/storage";
import { RECOVERY_STORAGE_KEY } from "constants/storage";

const ModalFrame = styled.div`
  // display: ${(props) => (props.$visible ? "flex" : "none")};
  position: fixed;
  top: 0;
  width: 100%;
  height: 100%;
  background: rgb(45, 49, 45, 0.5);
  z-index: 1010;
`;

const Modal = styled.div`
  position: relative;
  margin: 0 auto;
  top: 120px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  border-radius: 20px;
  width: 350px;
  height: 500px;
  background: #fafbfa;
  background: white;
  opacity: 1;
`;

const STEP = {
  VERIFY: 'verify',
  PASSWORD: 'password',
};

export const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { modalActions } = useContext(ModalContext);

  const usernameRef = useRef(null);
  const [step, setStep] = useState(STEP.VERIFY);
  const [username, setUsername] = useState('');
  const [channel, setChannel] = useState(() => 
    sessionStorageAdapter.load(RECOVERY_STORAGE_KEY.RESET_PASSWORD_CHANNEL) ?? 
    VERIFICATION_CHANNEL.EMAIL
  );
  
  const email = useVerification(VERIFICATION_CHANNEL.EMAIL);
  const mobile = useVerification(VERIFICATION_CHANNEL.MOBILE);

  const defaultUsername = 
    location.state?.account?.username ??
    sessionStorageAdapter.load(RECOVERY_STORAGE_KEY.RESET_PASSWORD_USERNAME);
  
  const validateUsername = () => {
    const usernameValue = usernameRef.current.value.trim();
    if (!usernameValue) {
      toast.error('아이디를 입력해주세요.');
      return;
    }
    return usernameValue;
  };

  const handleSendCode = async (e, verification) => {
    e.preventDefault();

    const username = validateUsername();
    if (!username) return;

    sessionStorageAdapter.save(RECOVERY_STORAGE_KEY.RESET_PASSWORD_USERNAME, username);

    await verification.sendCode();
  };

  const handleEmailVerify = async (e) => {
    e.preventDefault();
    
    const username = validateUsername();
    if (!username) return;

    const target = email.validateTarget();
    if (!target) return;

    const verificationCode = email.validateCode();
    if (!verificationCode) return;

    const payload = {
      channel: email.channel,
      target,
      verification_code: verificationCode,
      username,
    };

    await issueResetToken(payload);    
  };

  const handleMobileVerify = async (e) => {
    e.preventDefault();

    const username = validateUsername();
    if (!username) return;
    
    const target = mobile.validateTarget();
    if (!target) return;
    
    const verificationCode = mobile.validateCode();
    if (!verificationCode) return;

    const payload = {
      channel: mobile.channel,
      target,
      verification_code: verificationCode,
      username,
    };

    await issueResetToken(payload);    
  };

  const issueResetToken = async (payload) => {
    try {
      const response = await client.passwordResetToken(payload);
      log.debug('VERIFY_ACCOUNT_SUCCESS', response);
      setUsername(payload.username);
      clearCodeContext();
      setStep(STEP.PASSWORD);
    } catch(e) {
      log.debug('VERIFY_ACCOUNT_FAILED', e);
      const throttled = handleThrottle(e.response);
      if (throttled) return;
      const errors = parseErrors(e.response);
      toast.error(errors[0].message);
    }
  };
  
  const handlePasswordSubmit = async (password, password2) => {
    const payload = {
      password,
      password2,
    };

    try {
      const response = await client.resetPassword(payload);
      log.debug('CHANGE_PASSWORD_SUCCESS', response);
      toast.success('비밀번호가 변경되었습니다.');
      handleClose();
    } catch(e) {
      log.debug('CHANGE_PASSWORD_FAILED', e);
      const errors = parseErrors(e.response);
      toast.error(errors[0].message);
    }
  };

  const handleChannelChange = channel => {
    setChannel(channel);
    sessionStorageAdapter.save(RECOVERY_STORAGE_KEY.RESET_PASSWORD_CHANNEL, channel);
  };

  const clearCodeContext = () => {
    email.resetCodeContext();
    mobile.resetCodeContext();
  };

  const clearVerification = () => {
    email.reset();
    mobile.reset();
    sessionStorageAdapter.remove(RECOVERY_STORAGE_KEY.RESET_PASSWORD_CHANNEL);
    sessionStorageAdapter.remove(RECOVERY_STORAGE_KEY.RESET_PASSWORD_USERNAME);
  };
  
  const handleClose = () => {
    clearVerification();
    modalActions.openLogin();
    navigate(location.state?.from ?? '/');
  };

  return (
    <>
      <ModalFrame>
        <Modal>
          {step === STEP.VERIFY && (
            <ResetPasswordVerify
              usernameRef={usernameRef}
              defaultUsername={defaultUsername}
              email={email}
              mobile={mobile}
              channel={channel}
              onSendCode={handleSendCode}
              onEmailVerify={handleEmailVerify}
              onMobileVerify={handleMobileVerify}
              onChannelChange={handleChannelChange}
              onClose={handleClose}
            />
          )}

          {step === STEP.PASSWORD && (
            <ResetPasswordForm
              username={username}
              onPasswordSubmit={handlePasswordSubmit}
              onBack={() => setStep(STEP.VERIFY)}
              onClose={handleClose}
            />
          )}          
        </Modal>
      </ModalFrame>
    </>
  );
};